(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let collectionCardObserver;
  let productTabsObserver;
  let mediaCardObserver;
  let testimonialObserver;

  const revealCard = (card) => {
    card.classList.add('is-visible');
    if (collectionCardObserver) collectionCardObserver.unobserve(card);
  };

  const getCollectionCardObserver = () => {
    if (collectionCardObserver || !('IntersectionObserver' in window)) return collectionCardObserver;

    collectionCardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) revealCard(entry.target);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -7% 0px'
    });

    return collectionCardObserver;
  };

  const initCollectionReveals = (scope = document) => {
    const sectionSelector = '.bbs-collection-list--winter_cards, .bbs-collection-list--category_cards';
    const sections = [...scope.querySelectorAll(sectionSelector)];
    const observer = getCollectionCardObserver();

    if (scope.nodeType === 1 && scope.matches(sectionSelector)) sections.unshift(scope);

    sections.forEach((section) => section.querySelectorAll('.collection-item').forEach((card, index) => {
      if (card.dataset.bbsRevealReady) return;

      card.dataset.bbsRevealReady = 'true';
      card.style.setProperty('--bbs-reveal-delay', `${Math.min(index, 5) * 55}ms`);
      card.classList.add('bbs-reveal-card');

      if (reducedMotion || !observer) revealCard(card);
      else observer.observe(card);
    }));
  };

  const initTabs = (scope = document) => {
    scope.querySelectorAll('[data-bbs-product-tabs]').forEach((section) => {
      if (section.dataset.bbsTabsReady) return;

      section.dataset.bbsTabsReady = 'true';
      const tabs = [...section.querySelectorAll('[data-bbs-tab]')];
      const panels = [...section.querySelectorAll('[data-bbs-panel]')];
      const ctas = [...section.querySelectorAll('[data-bbs-tab-cta]')];

      const updateProgress = (panel) => {
        const track = panel && panel.querySelector('[data-bbs-tab-scroller]');
        const progress = panel && panel.querySelector('.bbs-product-tabs__progress');
        const indicator = progress && progress.querySelector('span');
        if (!track || !progress || !indicator) return;

        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const amount = maxScroll ? track.scrollLeft / maxScroll : 0;
        const clampedAmount = Math.max(0, Math.min(1, amount));
        const initialWidth = 35.82;
        indicator.style.width = `${initialWidth + clampedAmount * (100 - initialWidth)}%`;
        indicator.style.transform = 'none';
      };

      const animatePanel = (panel) => {
        if (!panel || reducedMotion) return;
        panel.classList.remove('is-animating');
        void panel.offsetWidth;
        panel.classList.add('is-animating');
      };

      const activateTab = (tab, moveFocus = false) => {
        let activePanel;

        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });

        panels.forEach((panel) => {
          const active = panel.dataset.bbsPanel === tab.dataset.bbsTab;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
          if (active) activePanel = panel;
        });

        const href = tab.dataset.bbsTabLink;
        const label = tab.dataset.bbsTabLinkText;
        ctas.forEach((cta) => {
          const ctaText = cta.querySelector('[data-bbs-tab-cta-text]');
          cta.hidden = !(href && label);
          if (href && label && ctaText) {
            cta.href = href;
            ctaText.textContent = label;
          } else {
            cta.removeAttribute('href');
          }
        });

        if (activePanel) {
          const track = activePanel.querySelector('[data-bbs-tab-scroller]');
          if (track) track.scrollLeft = 0;
          requestAnimationFrame(() => {
            updateProgress(activePanel);
            if (section.classList.contains('is-in-view')) animatePanel(activePanel);
          });
        }

        if (moveFocus) tab.focus();
      };

      section.bbsActivateTab = activateTab;

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateTab(tab));
        tab.addEventListener('keydown', (event) => {
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else return;

          event.preventDefault();
          activateTab(tabs[nextIndex], true);
        });
      });

      panels.forEach((panel) => {
        const track = panel.querySelector('[data-bbs-tab-scroller]');
        if (!track) return;
        track.addEventListener('scroll', () => updateProgress(panel), { passive: true });
      });

      const refreshActiveProgress = () => updateProgress(section.querySelector('[data-bbs-panel]:not([hidden])'));
      window.addEventListener('resize', refreshActiveProgress);

      section.classList.add('bbs-tabs-reveal-ready');
      if (reducedMotion || !('IntersectionObserver' in window)) {
        section.classList.add('is-in-view');
      } else {
        if (!productTabsObserver) {
          productTabsObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const currentSection = entry.target;
              currentSection.classList.add('is-in-view');
              const activePanel = currentSection.querySelector('[data-bbs-panel]:not([hidden])');
              if (activePanel && currentSection.bbsAnimatePanel) currentSection.bbsAnimatePanel(activePanel);
              productTabsObserver.unobserve(currentSection);
            });
          }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
        }
        section.bbsAnimatePanel = animatePanel;
        productTabsObserver.observe(section);
      }

      refreshActiveProgress();
    });
  };

  const initCollectionProgress = (scope = document) => {
    scope.querySelectorAll('.bbs-collection-list--category_cards').forEach((section) => {
      const scroller = section.querySelector('.scroller');
      const indicator = section.querySelector('.bbs-collection-progress span');
      const track = section.querySelector('.bbs-collection-progress');
      if (!scroller || !indicator || !track || scroller.dataset.bbsProgressReady) return;

      const update = () => {
        const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const progress = maxScroll ? scroller.scrollLeft / maxScroll : 0;
        const clampedProgress = Math.max(0, Math.min(1, progress));
        const initialWidth = 35.82;
        indicator.style.width = `${initialWidth + clampedProgress * (100 - initialWidth)}%`;
        indicator.style.transform = 'none';
      };

      scroller.dataset.bbsProgressReady = 'true';
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const initMediaCarousels = (scope = document) => {
    scope.querySelectorAll('[data-bbs-media-carousel]').forEach((section) => {
      if (section.dataset.bbsMediaReady) return;

      const track = section.querySelector('[data-bbs-media-track]');
      const progress = section.querySelector('.bbs-media-carousel__progress');
      const indicator = progress && progress.querySelector('span');
      const previous = section.querySelector('[data-bbs-media-prev]');
      const next = section.querySelector('[data-bbs-media-next]');
      const cards = [...section.querySelectorAll('.bbs-media-card')];
      if (!track || !progress || !indicator || !cards.length) return;

      section.dataset.bbsMediaReady = 'true';
      section.classList.add('bbs-media-reveal-ready');

      const update = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const amount = maxScroll ? track.scrollLeft / maxScroll : 0;
        const clampedAmount = Math.max(0, Math.min(1, amount));
        const initialWidth = 35.82;
        indicator.style.width = `${initialWidth + clampedAmount * (100 - initialWidth)}%`;
        indicator.style.transform = 'none';
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
      };

      const scrollCards = (direction) => {
        const firstCard = cards[0];
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        track.scrollBy({ left: direction * (firstCard.offsetWidth + gap), behavior: reducedMotion ? 'auto' : 'smooth' });
      };

      if (previous) previous.addEventListener('click', () => scrollCards(-1));
      if (next) next.addEventListener('click', () => scrollCards(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      cards.forEach((card, index) => {
        card.style.setProperty('--bbs-media-delay', `${Math.min(index, 3) * 65}ms`);
        if (reducedMotion || !('IntersectionObserver' in window)) {
          card.classList.add('is-visible');
          return;
        }

        if (!mediaCardObserver) {
          mediaCardObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              mediaCardObserver.unobserve(entry.target);
            });
          }, { threshold: 0.18, rootMargin: '0px 0px -5% 0px' });
        }
        mediaCardObserver.observe(card);
      });

      section.bbsMediaUpdate = update;
      update();
    });
  };

  const initTestimonials = (scope = document) => {
    scope.querySelectorAll('[data-bbs-testimonials]').forEach((section) => {
      if (section.dataset.bbsTestimonialsReady) return;

      const track = section.querySelector('[data-bbs-testimonials-track]');
      const progress = section.querySelector('.bbs-testimonials__progress');
      const indicator = progress && progress.querySelector('span');
      const previous = section.querySelector('[data-bbs-testimonials-prev]');
      const next = section.querySelector('[data-bbs-testimonials-next]');
      const cards = [...section.querySelectorAll('.bbs-review')];
      if (!track || !progress || !indicator || !cards.length) return;

      section.dataset.bbsTestimonialsReady = 'true';
      section.classList.add('bbs-testimonials-reveal-ready');

      const update = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const amount = maxScroll ? track.scrollLeft / maxScroll : 0;
        const clampedAmount = Math.max(0, Math.min(1, amount));
        const initialWidth = 35.82;
        indicator.style.width = `${initialWidth + clampedAmount * (100 - initialWidth)}%`;
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
      };

      const scrollCards = (direction) => {
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        track.scrollBy({ left: direction * (cards[0].offsetWidth + gap), behavior: reducedMotion ? 'auto' : 'smooth' });
      };

      if (previous) previous.addEventListener('click', () => scrollCards(-1));
      if (next) next.addEventListener('click', () => scrollCards(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      cards.forEach((card, index) => {
        card.style.setProperty('--bbs-review-delay', `${Math.min(index, 5) * 55}ms`);
        if (reducedMotion || !('IntersectionObserver' in window)) {
          card.classList.add('is-visible');
          return;
        }

        if (!testimonialObserver) {
          testimonialObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              testimonialObserver.unobserve(entry.target);
            });
          }, { threshold: 0.16, rootMargin: '0px 0px -5% 0px' });
        }
        testimonialObserver.observe(card);
      });

      section.bbsTestimonialsUpdate = update;
      update();
    });
  };

  const initHomepage = (scope = document) => {
    initTabs(scope);
    initCollectionProgress(scope);
    initCollectionReveals(scope);
    initMediaCarousels(scope);
    initTestimonials(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initHomepage());
  else initHomepage();
  document.addEventListener('shopify:section:load', (event) => initHomepage(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const mediaSection = event.target.closest && event.target.closest('[data-bbs-media-carousel]');
    const mediaCard = event.target.closest && event.target.closest('.bbs-media-card');
    if (mediaSection && mediaCard) {
      mediaCard.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      if (mediaSection.bbsMediaUpdate) requestAnimationFrame(mediaSection.bbsMediaUpdate);
    }

    const testimonialsSection = event.target.closest && event.target.closest('[data-bbs-testimonials]');
    const testimonialCard = event.target.closest && event.target.closest('.bbs-review');
    if (testimonialsSection && testimonialCard) {
      testimonialCard.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      if (testimonialsSection.bbsTestimonialsUpdate) requestAnimationFrame(testimonialsSection.bbsTestimonialsUpdate);
    }

    const section = event.target.closest && event.target.closest('[data-bbs-product-tabs]');
    if (!section || !section.bbsActivateTab) return;
    const selectedTab = [...section.querySelectorAll('[data-bbs-tab-block]')].find((tab) => tab.dataset.bbsTabBlock === event.detail.blockId);
    if (selectedTab) section.bbsActivateTab(selectedTab);
  });
})();
